/**
 * `slot_picker` model (spec-widget-mcp 05 Step 8): slots grouped by calendar day IN THE DECLARED
 * TIMEZONE (never the visitor's), each labelled `"14:30–15:00"` in that zone. Pure functions.
 */

/** A formatter for `timeZone`; an unknown zone (the server refuses one) falls back to UTC, never throws. */
function formatter(lang, timeZone, options) {
	try {
		return new Intl.DateTimeFormat(lang, { ...options, timeZone });
	} catch (_) {
		return new Intl.DateTimeFormat(lang, { ...options, timeZone: "UTC" });
	}
}

/** `YYYY-MM-DD` of an instant in `timeZone`. */
export const dayKey = (iso, timeZone) =>
	formatter("en-CA", timeZone, { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));

/** "Wednesday, 16 September" (in the visitor's language, the slot's zone). */
export const dayLabel = (iso, timeZone, lang = "en") =>
	formatter(lang, timeZone, { weekday: "long", day: "numeric", month: "long" }).format(new Date(iso));

const clock = (iso, timeZone) =>
	formatter("en-GB", timeZone, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso));

/** "09:20–09:40", plus ` · <label>` when the slot has one. */
export function slotLabel(slot, timeZone) {
	const range = `${clock(slot.start, timeZone)}–${clock(slot.end, timeZone)}`;
	return slot.label ? `${range} · ${slot.label}` : range;
}

/** A slot that has already started cannot be booked: shown disabled, never hidden. */
export const isPastSlot = (slot, now = Date.now()) => Date.parse(slot.start) <= now;

/**
 * @param {Array<{id: string, start: string, end: string, label?: string}>} slots
 * @param {string} timeZone IANA
 * @param {string} [lang]
 * @returns {Array<{ key: string, label: string, slots: object[] }>} days in time order
 */
export function groupSlotsByDay(slots, timeZone, lang = "en") {
	const sorted = [...(Array.isArray(slots) ? slots : [])].sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
	const days = [];
	for (const slot of sorted) {
		const key = dayKey(slot.start, timeZone);
		let day = days.find((d) => d.key === key);
		if (!day) {
			day = { key, label: dayLabel(slot.start, timeZone, lang), slots: [] };
			days.push(day);
		}
		day.slots.push(slot);
	}
	return days;
}
